import { Button, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import React from 'react';

import useTranslation from 'lib/hooks/useTranslation';
import isBrowser from 'lib/isBrowser';
import Menu from 'ui/shared/chakra/Menu';

type Language = {
  code: string;
  nameKey: string;
  flag: string;
};

const languages: Array<Language> = [
  { code: 'en', nameKey: 'settings.language_english', flag: '🇺🇸' },
  { code: 'ko', nameKey: 'settings.language_korean', flag: '🇰🇷' },
];

const LanguageSelector: React.FC = () => {
  const { t, changeLanguage, currentLanguage } = useTranslation();
  const isInBrowser = isBrowser();

  const handleLanguageChange = React.useCallback((code: string) => {
    if (!isInBrowser || code === currentLanguage) return;
    changeLanguage(code);
  }, [ changeLanguage, currentLanguage, isInBrowser ]);

  const handleEnglishClick = React.useCallback(() => {
    handleLanguageChange('en');
  }, [ handleLanguageChange ]);

  const handleKoreanClick = React.useCallback(() => {
    handleLanguageChange('ko');
  }, [ handleLanguageChange ]);

  const languageHandlers = React.useMemo(() => ({
    en: handleEnglishClick,
    ko: handleKoreanClick,
  }), [ handleEnglishClick, handleKoreanClick ]);

  const currentLang = React.useMemo(() => {
    return languages.find(lang => lang.code === currentLanguage) || languages[0];
  }, [ currentLanguage ]);

  return (
    <Menu isLazy>
      <MenuButton
        as={ Button }
        variant="ghost"
        size="sm"
        fontWeight="normal"
      >
        { currentLang.flag } { t(currentLang.nameKey) }
      </MenuButton>
      <MenuList minW="auto">
        { languages.map(lang => (
          <MenuItem
            key={ lang.code }
            onClick={ languageHandlers[lang.code as keyof typeof languageHandlers] }
            fontWeight={ currentLanguage === lang.code ? 'bold' : 'normal' }
          >
            { lang.flag } { t(lang.nameKey) }
          </MenuItem>
        )) }
      </MenuList>
    </Menu>
  );
};

export default React.memo(LanguageSelector);
